(() => {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'recently_viewed_products';
      this.currentProductHandle = this.dataset.productHandle;
      this.limit = Number.parseInt(this.dataset.limit, 10) || 8;
      this.showRating = this.dataset.showRating === 'true';
      this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';
      this.showVendor = this.dataset.showVendor === 'true';
      this.abortController = null;
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.saveCurrentProduct();
      this.loadRecentlyViewedProducts();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    isValidHandle(handle) {
      return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle);
    }

    getViewedProducts() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return [];

        return stored
          .map((item) => (typeof item === 'string' ? { handle: item } : item))
          .filter((item) => item && this.isValidHandle(item.handle));
      } catch (error) {
        console.error('Error reading recently viewed products:', error);
        return [];
      }
    }

    getCurrentProduct() {
      const dataElement = this.querySelector('[data-recently-viewed-current-product]');
      if (!dataElement) return null;

      try {
        const product = JSON.parse(dataElement.textContent);
        return this.isValidHandle(product.handle) ? product : null;
      } catch (error) {
        console.error('Error reading current product data:', error);
        return null;
      }
    }

    toStoredProduct(product) {
      return {
        handle: product.handle,
        title: product.title,
        url: product.url,
        vendor: product.vendor,
        featured_image: product.featured_image,
        images: Array.isArray(product.images) ? product.images.slice(0, 2) : [],
        price: product.price,
        compare_at_price: product.compare_at_price,
        available: product.available,
        tags: Array.isArray(product.tags) ? product.tags : [],
        rating_value: product.rating_value ?? null,
        rating_max: product.rating_max ?? null,
        rating_count: product.rating_count ?? 0,
      };
    }

    saveProducts(products) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(products.slice(0, this.limit + 1)));
      } catch (error) {
        console.error('Error saving recently viewed products:', error);
      }
    }

    saveCurrentProduct() {
      if (!this.isValidHandle(this.currentProductHandle)) return;

      const currentProduct = this.getCurrentProduct() || { handle: this.currentProductHandle };
      const viewedProducts = this.getViewedProducts().filter(
        ({ handle }) => handle !== this.currentProductHandle,
      );

      viewedProducts.unshift(this.toStoredProduct(currentProduct));
      this.saveProducts(viewedProducts);
    }

    cacheFetchedProducts(products) {
      const productsByHandle = new Map(products.map((product) => [product.handle, this.toStoredProduct(product)]));
      const updated = this.getViewedProducts().map((product) => productsByHandle.get(product.handle) || product);
      this.saveProducts(updated);
    }

    async loadRecentlyViewedProducts() {
      const viewedProducts = this.getViewedProducts()
        .filter(({ handle }) => handle !== this.currentProductHandle)
        .slice(0, this.limit);

      if (viewedProducts.length === 0) {
        this.hidden = true;
        return;
      }

      this.showLoading();

      try {
        const products = await this.fetchProducts(viewedProducts);

        const renderedProductsCount = this.renderProducts(products);
        this.hidden = renderedProductsCount === 0;
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Error loading recently viewed products:', error);
        this.hidden = true;
      } finally {
        this.hideLoading();
      }
    }

    async fetchProducts(entries) {
      const root = window.Shopify?.routes?.root || '/';
      const products = await Promise.all(
        entries.map(async (entry) => {
          if (entry.title && typeof entry.price === 'number') return entry;

          try {
            const response = await fetch(`${root}products/${encodeURIComponent(entry.handle)}.js`, {
              signal: this.abortController?.signal,
            });
            if (!response.ok) return null;

            const product = await response.json();
            return {
              ...product,
              rating_value: entry.rating_value ?? null,
              rating_max: entry.rating_max ?? null,
              rating_count: entry.rating_count ?? 0,
            };
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            console.error(`Error fetching product ${entry.handle}:`, error);
            return null;
          }
        }),
      );

      const validProducts = products.filter(Boolean);
      this.cacheFetchedProducts(validProducts);
      return validProducts;
    }

    createCardElement(product) {
      const template = document.createElement('template');
      template.innerHTML = this.createProductCard(product).trim();
      return template.content.firstElementChild;
    }

    renderProducts(products) {
      const gridContainer = this.querySelector('.recently-viewed-products__grid');
      const sliderContainer = this.querySelector('.recently-viewed-products__slider-list');
      const gridFragment = document.createDocumentFragment();
      const sliderFragment = document.createDocumentFragment();
      let renderedProductsCount = 0;

      products.forEach((product) => {
        const card = this.createCardElement(product);
        if (!card) return;
        renderedProductsCount += 1;

        if (gridContainer) gridFragment.appendChild(card);
        if (sliderContainer) {
          const sliderCard = card.cloneNode(true);
          sliderCard.classList.add('slider__slide');
          sliderFragment.appendChild(sliderCard);
        }
      });

      if (gridContainer) gridContainer.replaceChildren(gridFragment);
      if (sliderContainer) sliderContainer.replaceChildren(sliderFragment);

      this.forceMobileStyles();
      window.blushSplitTitles?.(this);
      return renderedProductsCount;
    }

    forceMobileStyles() {
      const sliderList = this.querySelector('.recently-viewed-products__slider-list');
      sliderList?.classList.add('slider', 'slider--mobile', 'grid--peek', 'mobile-compact');
    }

    safeProductUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '#';
      } catch (_error) {
        return '#';
      }
    }

    safeImageUrl(value) {
      if (!value) return null;

      try {
        const url = new URL(value, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return null;
        if (url.hostname.endsWith('shopify.com') || url.hostname === window.location.hostname) {
          url.searchParams.set('width', '533');
        }
        return url.href;
      } catch (_error) {
        return null;
      }
    }

    createProductCard(product) {
      const images = Array.isArray(product.images) ? product.images : [];
      const featuredImage =
        typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.src;
      const firstImage = this.safeImageUrl(featuredImage || images[0]?.src || images[0]);
      const secondImage = this.showSecondaryImage
        ? this.safeImageUrl(images[1]?.src || images[1])
        : null;
      const price = this.formatMoney(product.price);
      const compareAtPrice =
        product.compare_at_price && product.compare_at_price > product.price
          ? this.formatMoney(product.compare_at_price)
          : null;
      const tags = Array.isArray(product.tags) ? product.tags : [];
      const hasSaleBadge = tags.includes('sale');
      const hasHitBadge = tags.includes('hit');
      // Same labels as the Liquid cards (snippets/blush-badges.liquid).
      const isRu = document.documentElement.lang === 'ru';
      const escapedTitle = this.escapeHtml(product.title);
      const escapedVendor = this.showVendor && product.vendor ? this.escapeHtml(product.vendor) : '';
      const productUrl = this.escapeHtml(this.safeProductUrl(product.url));
      let cardHTML = '<li class="grid__item">';

      cardHTML += '<div class="card-wrapper product-card-wrapper underline-links-hover">';
      cardHTML += '<div class="card card--standard card--media" style="--ratio-percent: 100%;">';
      cardHTML += '<div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: 100%;">';

      if (firstImage) {
        cardHTML += '<div class="card__media"><div class="media media--transparent media--hover-effect">';
        cardHTML += `<img src="${this.escapeHtml(firstImage)}" alt="${escapedTitle}" class="motion-reduce" loading="lazy" decoding="async" width="533" height="533">`;
        if (secondImage) {
          cardHTML += `<img src="${this.escapeHtml(secondImage)}" alt="${escapedTitle}" class="motion-reduce" loading="lazy" decoding="async" width="533" height="533">`;
        }
        cardHTML += '</div></div>';
      }

      if (hasSaleBadge || hasHitBadge) {
        cardHTML += '<div class="card__badge bottom left">';
        if (hasSaleBadge) cardHTML += `<div class="sale-badge"><span>${isRu ? 'Акция' : 'Акція'}</span></div>`;
        if (hasHitBadge) cardHTML += `<div class="hit-badge"><span>${isRu ? 'Хит' : 'Хіт'}</span></div>`;
        cardHTML += '</div>';
      }

      cardHTML += '</div><div class="card__content"><div class="card__information">';
      cardHTML += `<h3 class="card__heading h5"><a href="${productUrl}" class="full-unstyled-link">${escapedTitle}</a></h3>`;
      cardHTML += '<div class="card-information">';

      if (escapedVendor) cardHTML += `<div class="caption-with-letter-spacing light">${escapedVendor}</div>`;

      if (this.showRating && Number(product.rating_value) > 0) {
        const ratingValue = Number(product.rating_value);
        const ratingMax = Number(product.rating_max) || 5;
        const ratingCount = Number(product.rating_count) || 0;
        const ratingFloor = Math.floor(ratingValue);
        const remainder = ratingValue % 1;
        const ratingDecimal = remainder >= 0.3 && remainder <= 0.7 ? 0.5 : remainder > 0.7 ? 1 : 0;

        cardHTML += `<div class="rating" role="img" aria-label="Рейтинг ${ratingValue} ${isRu ? 'из' : 'з'} ${ratingMax}">`;
        cardHTML += `<span aria-hidden="true" class="rating-star" style="--rating: ${ratingFloor}; --rating-max: ${ratingMax}; --rating-decimal: ${ratingDecimal};"></span></div>`;
        cardHTML += `<p class="rating-text caption"><span aria-hidden="true">${ratingValue} / ${ratingMax}</span></p>`;
        if (ratingCount > 0) {
          cardHTML += `<p class="rating-count caption"><span aria-hidden="true">(${ratingCount})</span></p>`;
        }
      }

      cardHTML += `<div class="price ${compareAtPrice ? 'price--on-sale' : ''}"><div class="price__container">`;
      if (compareAtPrice) {
        cardHTML += `<div class="price__regular"><span class="price-item price-item--regular">${compareAtPrice}</span></div>`;
        cardHTML += `<div class="price__sale"><span class="price-item price-item--sale price-item--last">${price}</span></div>`;
      } else {
        cardHTML += `<div class="price__regular"><span class="price-item price-item--regular">${price}</span></div>`;
      }

      cardHTML += '</div></div></div></div></div></div></div></li>';
      return cardHTML;
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value ?? '';
      return element.innerHTML;
    }

    formatMoney(cents) {
      const amount = Number(cents) / 100;
      try {
        return new Intl.NumberFormat('uk-UA', {
          style: 'currency',
          currency: 'UAH',
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ₴`;
      }
    }

    showLoading() {
      const loader = this.querySelector('.recently-viewed-products__loading');
      if (loader) loader.style.display = 'block';
    }

    hideLoading() {
      const loader = this.querySelector('.recently-viewed-products__loading');
      if (loader) loader.style.display = 'none';
    }

  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
